/**
 * Bottom sheet: conversation with user (messenger-style).
 * Same UX as go-supply invite sheets: title, scrollable content, footer with actions.
 */

import { useState, useEffect, useRef } from "react";
import {
  graphql,
  getErrorMessage,
  ADMIN_ORDERS_BY_ACCOUNT_QUERY,
  ADMIN_USER_CONVERSATION_QUERY,
  ADMIN_SEND_ORDER_MESSAGE_MUTATION,
  ADMIN_UPCOMING_EVENTS_QUERY,
} from "../../../../lib/graphql.ts";
import { safeShowAlert } from "../../../../lib/telegram.ts";
import { storageLog } from "../../../../lib/logger.ts";
import { safeJsonParse } from "../../../../lib/json-utils.ts";
import { formatDateBangkok } from "../../../../lib/bangkok-time.ts";
import {
  getOrderStatusConfig,
  getCurrencySymbol,
  formatCustomerName,
  type AdminOrder,
} from "../../shared/types.ts";
import { BottomSheet } from "../../../../components/ui/BottomSheet.tsx";
import { IconTicket, IconCalendar } from "../../../../components/Icons.tsx";

const DEFAULT_BUTTON_TEXT_ORDER = "Перейти к заказу";
const DEFAULT_BUTTON_TEXT_EVENT = "Перейти к мероприятию";
const STORAGE_KEY_PREFIX = "one-church:send-order-message:";

/** Короткий номер заказа для отображения (последние 6 символов id, как в боте) */
function formatOrderNumber(orderId: string): string {
  return "#" + orderId.slice(-6).toUpperCase();
}

type MessageAttachment = "nothing" | "cover";
type AttachmentKind = "nothing" | "cover" | "tickets";

interface ModalFormState {
  message: string;
  addOrderButton: boolean;
  addEventButton: boolean;
  buttonText: string;
  buttonTextEvent: string;
  linkOrderId: string;
  linkEventId: string | null;
  messageAttachment: MessageAttachment;
  sendTicketsSeparately: boolean;
  /** @deprecated use messageAttachment + sendTicketsSeparately; kept for persist/load */
  attachmentKind?: AttachmentKind;
}

function loadFormState(accountId: string): Partial<ModalFormState> | null {
  const raw = sessionStorage.getItem(STORAGE_KEY_PREFIX + accountId);
  if (!raw) return null;
  
  // Use safe JSON parse with default null - returns null for malformed JSON
  const parsed = safeJsonParse<Partial<ModalFormState> | null>(raw, null);
  if (!parsed || typeof parsed !== "object") {
    storageLog.debug("Invalid form state format in storage");
    return null;
  }
  return parsed;
}

function saveFormState(accountId: string, state: ModalFormState): void {
  try {
    sessionStorage.setItem(
      STORAGE_KEY_PREFIX + accountId,
      JSON.stringify(state)
    );
  } catch (error) {
    // sessionStorage may fail in private browsing
    storageLog.debug("Failed to save form state:", error);
  }
}

interface SendOrderMessageModalProps {
  order: AdminOrder;
  onClose: () => void;
  onSent?: () => void;
}

interface OrderStatusCount {
  status: string;
  count: number;
}

interface OrderWithTickets {
  id: string;
  status: string;
  totalAmount: string;
  currency: string;
  createdAt: string;
  event: { id: string; title: string; startsAt: string };
  tickets: Array<{ id: string }>;
}

interface OrdersByAccountResult {
  adminOrdersByAccount: {
    orders: OrderWithTickets[];
    totalCount: number;
    statusCounts: OrderStatusCount[];
  };
}

interface ConversationMessage {
  id: string;
  messageText: string;
  orderId: string | null;
  buttonText: string | null;
  buttonTextEvent: string | null;
  linkOrderId: string | null;
  linkEventId: string | null;
  createdAt: string;
  sentBy: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    username: string | null;
  } | null;
}

function getInitialState(
  saved: Partial<ModalFormState> | null,
  orderId: string
): Partial<ModalFormState> {
  const fromSaved = saved ?? {};
  const linkType = (fromSaved as { linkType?: "order" | "event" }).linkType;
  const attachTickets = (fromSaved as { attachTickets?: boolean })
    .attachTickets;
  const attachmentKind =
    fromSaved.attachmentKind ??
    (attachTickets
      ? "tickets"
      : linkType === "order" || linkType === "event"
      ? "cover"
      : "nothing");
  return {
    addOrderButton: fromSaved.addOrderButton ?? linkType === "order",
    addEventButton: fromSaved.addEventButton ?? linkType === "event",
    buttonText: fromSaved.buttonText ?? DEFAULT_BUTTON_TEXT_ORDER,
    buttonTextEvent: fromSaved.buttonTextEvent ?? DEFAULT_BUTTON_TEXT_EVENT,
    linkOrderId: fromSaved.linkOrderId ?? orderId,
    linkEventId: fromSaved.linkEventId ?? null,
    messageAttachment:
      fromSaved.messageAttachment ??
      (attachmentKind === "cover" ? "cover" : "nothing"),
    sendTicketsSeparately:
      fromSaved.sendTicketsSeparately ?? attachmentKind === "tickets",
  };
}

export function SendOrderMessageModal({
  order,
  onClose,
  onSent,
}: SendOrderMessageModalProps) {
  const accountId = order.customer.id;
  const saved = loadFormState(accountId);
  const initial = getInitialState(saved, order.id);

  const [message, setMessage] = useState(saved?.message ?? "");
  const [addOrderButton, setAddOrderButton] = useState(
    initial.addOrderButton ?? false
  );
  const [addEventButton, setAddEventButton] = useState(
    initial.addEventButton ?? false
  );
  const [buttonText, setButtonText] = useState(
    initial.buttonText ?? DEFAULT_BUTTON_TEXT_ORDER
  );
  const [buttonTextEvent, setButtonTextEvent] = useState(
    initial.buttonTextEvent ?? DEFAULT_BUTTON_TEXT_EVENT
  );
  const [linkOrderId, setLinkOrderId] = useState(
    initial.linkOrderId ?? order.id
  );
  const [linkEventId, setLinkEventId] = useState<string | null>(
    initial.linkEventId ?? null
  );
  const [messageAttachment, setMessageAttachment] = useState<MessageAttachment>(
    initial.messageAttachment ?? "nothing"
  );
  const [sendTicketsSeparately, setSendTicketsSeparately] = useState(
    initial.sendTicketsSeparately ?? false
  );
  const [accountOrders, setAccountOrders] = useState<OrderWithTickets[]>([]);
  const [events, setEvents] = useState<
    Array<{ id: string; title: string; startsAt: string }>
  >([]);
  const [statusCounts, setStatusCounts] = useState<OrderStatusCount[]>([]);
  const [conversationMessages, setConversationMessages] = useState<
    ConversationMessage[]
  >([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(true);
  const [sending, setSending] = useState(false);
  const [orderSelectOpen, setOrderSelectOpen] = useState(false);
  const [eventSelectOpen, setEventSelectOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const orderSelectRef = useRef<HTMLDivElement>(null);
  const eventSelectRef = useRef<HTMLDivElement>(null);
  const ticketsBlockRef = useRef<HTMLDivElement>(null);

  const selectedOrder = accountOrders.find((o) => o.id === linkOrderId);
  const selectedEvent = events.find((e) => e.id === linkEventId);
  const selectedOrderHasTickets = (selectedOrder?.tickets?.length ?? 0) > 0;

  useEffect(() => {
    saveFormState(accountId, {
      message,
      addOrderButton,
      addEventButton,
      buttonText,
      buttonTextEvent,
      linkOrderId,
      linkEventId,
      messageAttachment,
      sendTicketsSeparately,
      attachmentKind: sendTicketsSeparately ? "tickets" : messageAttachment,
    });
  }, [
    accountId,
    message,
    addOrderButton,
    addEventButton,
    buttonText,
    buttonTextEvent,
    linkOrderId,
    linkEventId,
    messageAttachment,
    sendTicketsSeparately,
  ]);

  // Load upcoming events for link selector (только активные, без прошедших); добавляем мероприятие заказа, если его нет в списке
  useEffect(() => {
    let cancelled = false;
    setLoadingEvents(true);
    graphql<{
      adminUpcomingEvents: Array<{
        id: string;
        title: string;
        startsAt: string;
      }>;
    }>(ADMIN_UPCOMING_EVENTS_QUERY, { limit: 50 })
      .then((data) => {
        if (cancelled) return;
        const list = data.adminUpcomingEvents ?? [];
        const hasOrderEvent = list.some((e) => e.id === order.event.id);
        const eventsList = hasOrderEvent
          ? list
          : [
              {
                id: order.event.id,
                title: order.event.title,
                startsAt: order.event.startsAt,
              },
              ...list,
            ];
        setEvents(eventsList);
      })
      .catch(() => {
        if (!cancelled) setEvents([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingEvents(false);
      });
    return () => {
      cancelled = true;
    };
  }, [order.event.id, order.event.title, order.event.startsAt]);

  // Close order dropdown on click outside
  useEffect(() => {
    if (!orderSelectOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        orderSelectRef.current &&
        !orderSelectRef.current.contains(e.target as Node)
      ) {
        setOrderSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [orderSelectOpen]);

  // Close event dropdown on click outside
  useEffect(() => {
    if (!eventSelectOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        eventSelectRef.current &&
        !eventSelectRef.current.contains(e.target as Node)
      ) {
        setEventSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [eventSelectOpen]);

  // При включении кнопки «Мероприятие» подставляем мероприятие из заказа; иначе первое из списка
  useEffect(() => {
    if (!addEventButton || linkEventId || events.length === 0) return;
    const orderEventInList = events.find((e) => e.id === order.event.id);
    setLinkEventId(orderEventInList ? order.event.id : events[0].id);
  }, [addEventButton, linkEventId, events, order.event.id]);

  // Сброс обложки, если нет выбранного мероприятия (обложка только в блоке «кнопка на мероприятие»)
  useEffect(() => {
    if (messageAttachment === "cover" && !(addEventButton && linkEventId))
      setMessageAttachment("nothing");
    if (
      sendTicketsSeparately &&
      (order.tickets?.length ?? 0) === 0 &&
      !selectedOrderHasTickets
    )
      setSendTicketsSeparately(false);
  }, [
    messageAttachment,
    sendTicketsSeparately,
    addEventButton,
    linkEventId,
    order.tickets?.length,
    selectedOrderHasTickets,
  ]);

  // Скролл к раскрытому блоку при включении чекбокса
  useEffect(() => {
    if (!addOrderButton) return;
    const t = setTimeout(() => {
      orderSelectRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    return () => clearTimeout(t);
  }, [addOrderButton]);

  useEffect(() => {
    if (!addEventButton) return;
    const t = setTimeout(() => {
      eventSelectRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    return () => clearTimeout(t);
  }, [addEventButton]);

  useEffect(() => {
    if (!sendTicketsSeparately) return;
    const t = setTimeout(() => {
      ticketsBlockRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
    return () => clearTimeout(t);
  }, [sendTicketsSeparately]);

  useEffect(() => {
    let cancelled = false;
    setLoadingOrders(true);
    graphql<OrdersByAccountResult>(ADMIN_ORDERS_BY_ACCOUNT_QUERY, {
      accountId,
      limit: 30,
    })
      .then((data) => {
        if (!cancelled && data.adminOrdersByAccount) {
          const { orders: list, statusCounts: counts } =
            data.adminOrdersByAccount;
          setAccountOrders(list ?? []);
          setStatusCounts(counts ?? []);
          if (list && !list.some((o) => o.id === linkOrderId)) {
            setLinkOrderId(order.id);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAccountOrders([]);
          setStatusCounts([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOrders(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, order.id]);

  useEffect(() => {
    let cancelled = false;
    setLoadingConversation(true);
    graphql<{ adminUserConversation: ConversationMessage[] }>(
      ADMIN_USER_CONVERSATION_QUERY,
      {
        accountId,
        limit: 50,
      }
    )
      .then((data) => {
        if (!cancelled) {
          setConversationMessages(data.adminUserConversation ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) setConversationMessages([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingConversation(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationMessages.length]);

  const hasTicketsCount =
    statusCounts.find(
      (s) => s.status === "TICKET_ISSUED" || s.status === "TICKETS_PENDING_SEND"
    )?.count ?? 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = message.trim();
    if (!text) {
      safeShowAlert("Введите текст сообщения");
      return;
    }
    if (addEventButton && !linkEventId) {
      safeShowAlert("Выберите мероприятие");
      return;
    }
    if (
      sendTicketsSeparately &&
      (order.tickets?.length ?? 0) === 0 &&
      !selectedOrderHasTickets
    ) {
      safeShowAlert(
        "Выберите заказ с билетами для отправки отдельным сообщением."
      );
      return;
    }
    setSending(true);
    try {
      const attachmentKindArg = sendTicketsSeparately
        ? "TICKETS"
        : messageAttachment === "cover"
        ? "COVER"
        : "NOTHING";
      const data = await graphql<{
        adminSendOrderMessage: { success: boolean; message: string };
      }>(ADMIN_SEND_ORDER_MESSAGE_MUTATION, {
        orderId: order.id,
        message: text,
        buttonText: buttonText.trim() || DEFAULT_BUTTON_TEXT_ORDER,
        buttonTextEvent: buttonTextEvent.trim() || DEFAULT_BUTTON_TEXT_EVENT,
        linkOrderId: addOrderButton ? linkOrderId : null,
        linkEventId: addEventButton ? linkEventId : null,
        attachmentKind: attachmentKindArg,
      });
      const result = data.adminSendOrderMessage;
      if (!result.success) {
        safeShowAlert(result.message ?? "Ошибка отправки");
        return;
      }
      safeShowAlert(result.message ?? "Отправлено");
      onSent?.();
      onClose();
    } catch (err) {
      safeShowAlert(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const customerName = formatCustomerName(order.customer);
  const FORM_ID = "send-order-message-form";

  return (
    <BottomSheet
      open
      onOpenChange={(o) => !o && onClose()}
      title={customerName}
      titleId="send-order-message-title"
      compact
      footer={
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 px-4 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50 active:bg-slate-100"
          >
            Отмена
          </button>
          <button
            type="submit"
            form={FORM_ID}
            disabled={sending}
            className="flex-1 py-2.5 px-4 rounded-xl bg-amber-500 text-white font-medium hover:bg-amber-600 disabled:opacity-50 disabled:pointer-events-none"
          >
            {sending ? "Отправка…" : "Отправить"}
          </button>
        </div>
      }
    >
      {/* 1. Status counts (first content block) */}
      <div className="text-[12px] text-slate-500 mb-3">
        {loadingOrders ? (
          <span>Загрузка…</span>
        ) : statusCounts.length === 0 ? (
          <span>Заказов нет</span>
        ) : (
          <span>
            {statusCounts.map((s, i) => {
              const config = getOrderStatusConfig(s.status);
              const isTickets =
                s.status === "TICKET_ISSUED" ||
                s.status === "TICKETS_PENDING_SEND";
              return (
                <span key={s.status}>
                  {i > 0 && " · "}
                  <span
                    className={
                      isTickets && hasTicketsCount > 0
                        ? "font-medium text-teal-600"
                        : "text-slate-500"
                    }
                    title={
                      isTickets && hasTicketsCount > 0
                        ? "Есть билеты"
                        : undefined
                    }
                  >
                    {config.label}: {s.count}
                    {isTickets && hasTicketsCount > 0 ? " ✓" : ""}
                  </span>
                </span>
              );
            })}
          </span>
        )}
      </div>

      {/* 2. Message thread */}
      <div className="space-y-3 mb-4">
        {loadingConversation ? (
          <p className="text-sm text-slate-500">Загрузка переписки…</p>
        ) : conversationMessages.length === 0 ? (
          <p className="text-sm text-slate-500">Сообщений пока нет</p>
        ) : (
          conversationMessages.map((msg) => {
            const fromUser = msg.sentBy === null;
            return (
              <div
                key={msg.id}
                className={`flex flex-col gap-0.5 ${
                  fromUser ? "items-start" : "items-end"
                }`}
              >
                <div
                  className={`max-w-[85%] px-3 py-2 rounded-xl text-[13px] ${
                    fromUser
                      ? "bg-slate-200/80 text-slate-800"
                      : "bg-amber-100 text-amber-900"
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">
                    {msg.messageText}
                  </p>
                  {(msg.buttonText || msg.buttonTextEvent) && (
                    <p className="text-[11px] text-slate-500 mt-1">
                      Кнопки:{" "}
                      {[msg.buttonText, msg.buttonTextEvent]
                        .filter(Boolean)
                        .map((t) => `«${t}»`)
                        .join(", ")}
                    </p>
                  )}
                </div>
                <span className="text-[10px] text-slate-400">
                  {formatDateBangkok(msg.createdAt, {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                  {msg.sentBy && ` · ${formatCustomerName(msg.sentBy)}`}
                </span>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* 3. Form fields (submit button is in BottomSheet footer via form=FORM_ID) */}
      <form
        id={FORM_ID}
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        <div>
          <label
            htmlFor="send-message-text"
            className="block text-sm font-medium text-slate-700 mb-1"
          >
            Текст сообщения
          </label>
          <textarea
            id="send-message-text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Сообщение придёт в Telegram…"
            rows={3}
            className="input-field w-full resize-none"
            required
          />
        </div>

        <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400">
          Кнопки в сообщении (кнопка «Ответить» добавится автоматически)
        </p>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={addOrderButton}
            onChange={(e) => setAddOrderButton(e.target.checked)}
            className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
          />
          <span className="text-sm font-medium text-slate-700">
            Добавить кнопку на заказ
          </span>
        </label>
        {addOrderButton && (
          <div className="space-y-2 mb-3">
            <label
              htmlFor="send-message-button-order"
              className="block text-sm text-slate-600"
            >
              Текст кнопки
            </label>
            <input
              id="send-message-button-order"
              type="text"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder={DEFAULT_BUTTON_TEXT_ORDER}
              className="input-field"
            />
            <div ref={orderSelectRef}>
              {loadingOrders ? (
                <p className="text-sm text-slate-500 py-2">Загрузка заказов…</p>
              ) : accountOrders.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">Нет заказов</p>
              ) : (
                <div className="space-y-3">
                  {selectedOrder && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-accent)] text-white">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <IconTicket className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-[15px]">
                          {formatOrderNumber(selectedOrder.id)}{" "}
                          {selectedOrder.event.title}
                        </p>
                        <p className="text-[13px] opacity-90">
                          {formatDateBangkok(selectedOrder.event.startsAt, {
                            day: "numeric",
                            month: "short",
                          })}
                          {" · "}
                          {selectedOrder.totalAmount}{" "}
                          {getCurrencySymbol(selectedOrder.currency)}
                          {" · "}
                          {getOrderStatusConfig(selectedOrder.status).label}
                          {selectedOrder.id === order.id ? " · текущий" : ""}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setOrderSelectOpen((v) => !v)}
                        className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-white/20 text-[13px] font-medium active:bg-white/30"
                        aria-label={
                          orderSelectOpen ? "Свернуть" : "Сменить заказ"
                        }
                      >
                        {orderSelectOpen ? "Свернуть" : "Сменить"}
                      </button>
                    </div>
                  )}
                  {orderSelectOpen && (
                    <div className="max-h-[240px] overflow-y-auto space-y-1 -mx-1 px-1">
                      {accountOrders.map((o) => {
                        const statusConfig = getOrderStatusConfig(o.status);
                        const sym = getCurrencySymbol(o.currency);
                        const dateStr = formatDateBangkok(o.event.startsAt, {
                          day: "numeric",
                          month: "short",
                        });
                        const isSelected = o.id === linkOrderId;
                        return (
                          <button
                            key={o.id}
                            type="button"
                            onClick={() => {
                              setLinkOrderId(o.id);
                              setOrderSelectOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 min-h-[52px] px-3 py-2 rounded-xl border text-left transition-colors ${
                              isSelected
                                ? "border-[var(--color-accent)] bg-[var(--color-accent)]/10"
                                : "bg-slate-50 border-slate-200 active:bg-slate-100"
                            }`}
                          >
                            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                              <IconTicket className="w-4 h-4 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate text-[14px]">
                                {formatOrderNumber(o.id)} {o.event.title}
                              </p>
                              <p className="text-[12px] text-slate-500 truncate">
                                {dateStr} · {o.totalAmount} {sym} ·{" "}
                                {statusConfig.label}
                                {o.id === order.id ? " · текущий" : ""}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={addEventButton}
            onChange={(e) => setAddEventButton(e.target.checked)}
            className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
          />
          <span className="text-sm font-medium text-slate-700">
            Добавить кнопку на мероприятие
          </span>
        </label>
        {addEventButton && (
          <div className="space-y-2 mb-3">
            <label
              htmlFor="send-message-button-event"
              className="block text-sm text-slate-600"
            >
              Текст кнопки
            </label>
            <input
              id="send-message-button-event"
              type="text"
              value={buttonTextEvent}
              onChange={(e) => setButtonTextEvent(e.target.value)}
              placeholder={DEFAULT_BUTTON_TEXT_EVENT}
              className="input-field"
            />
            <div ref={eventSelectRef}>
              {loadingEvents ? (
                <p className="text-sm text-slate-500 py-2">
                  Загрузка мероприятий…
                </p>
              ) : events.length === 0 ? (
                <p className="text-sm text-slate-500 py-2">
                  Нет предстоящих мероприятий
                </p>
              ) : (
                <div className="space-y-3">
                  {selectedEvent && (
                    <div className="flex items-center gap-3 p-3 rounded-xl bg-[var(--color-accent)] text-white">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <IconCalendar className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-[15px]">
                          {selectedEvent.title}
                        </p>
                        <p className="text-[13px] opacity-90">
                          {formatDateBangkok(selectedEvent.startsAt, {
                            day: "numeric",
                            month: "short",
                          })}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEventSelectOpen((v) => !v)}
                        className="flex-shrink-0 px-3 py-1.5 rounded-lg bg-white/20 text-[13px] font-medium active:bg-white/30"
                        aria-label={
                          eventSelectOpen ? "Свернуть" : "Сменить мероприятие"
                        }
                      >
                        {eventSelectOpen ? "Свернуть" : "Сменить"}
                      </button>
                    </div>
                  )}
                  {eventSelectOpen && (
                    <div className="max-h-[240px] overflow-y-auto space-y-1 -mx-1 px-1">
                      {events.map((ev) => {
                        const isSelected = ev.id === linkEventId;
                        return (
                          <button
                            key={ev.id}
                            type="button"
                            onClick={() => {
                              setLinkEventId(ev.id);
                              setEventSelectOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 min-h-[52px] px-3 py-2 rounded-xl border text-left transition-colors ${
                              isSelected
                                ? "border-[var(--color-accent)] bg-[var(--color-accent)]/10"
                                : "bg-slate-50 border-slate-200 active:bg-slate-100"
                            }`}
                          >
                            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                              <IconCalendar className="w-4 h-4 text-slate-500" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 truncate text-[14px]">
                                {ev.title}
                              </p>
                              <p className="text-[12px] text-slate-500 truncate">
                                {formatDateBangkok(ev.startsAt, {
                                  day: "numeric",
                                  month: "short",
                                })}
                              </p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {selectedEvent && (
                    <label className="flex items-center gap-2 cursor-pointer mt-3">
                      <input
                        type="checkbox"
                        checked={messageAttachment === "cover"}
                        onChange={(e) =>
                          setMessageAttachment(e.target.checked ? "cover" : "nothing")
                        }
                        className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm text-slate-700">
                        Прикрепить обложку мероприятия
                      </span>
                    </label>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {(order.tickets?.length ?? 0) > 0 && (
          <div ref={ticketsBlockRef}>
            <p className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-2">
              Отдельным сообщением
            </p>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={sendTicketsSeparately}
                onChange={(e) => setSendTicketsSeparately(e.target.checked)}
                className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
              />
              <span className="text-sm text-slate-700">
                Отправить билеты отдельным сообщением
              </span>
            </label>
          </div>
        )}
      </form>
    </BottomSheet>
  );
}
